import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { updateRentalPortalApplicantDocument } from '../../../../../../../api'
import { useNotification } from '../../../../../../../hooks'
import { toApplicantDocumentVerdictData } from '../../../../../../RentalPortalApplicant'
import { useApplicantProfileContext } from '../../../../RentalPortalApplicantProfile.hooks'
import { APPLICANT_DOCUMENT_STATUS } from '../../ApplicantDocumentChecklist.constants'
import { ApplicantDocumentRejectModal } from '../../ApplicantDocumentRejectModal'

export function useApplicantDocumentVerdict() {
  const { t } = useTranslation()

  const { handleAddErrorNotification } = useNotification()

  const { applicantId, handleCloseModal, handleEntityReload, setModal } =
    useApplicantProfileContext()

  const submittingKeyRef = useRef(null)

  const [submittingKey, setSubmittingKey] = useState(null)

  const [submittingStatus, setSubmittingStatus] = useState(null)

  function setSubmitting({ key, status }) {
    submittingKeyRef.current = key

    setSubmittingKey(key)
    setSubmittingStatus(status)
  }

  async function saveVerdict({ key, rejectedReason, status }) {
    setSubmitting({ key, status })

    try {
      await updateRentalPortalApplicantDocument({
        data: toApplicantDocumentVerdictData({ rejectedReason, status }),
        id: applicantId,
        key,
      })

      await handleEntityReload()
    } catch {
      handleAddErrorNotification({
        textButtonValue: t('RentalPortal.ListingRetryBtnKey'),
        title: t('RentalPortal.ApplicantDocumentVerdictErrorKey'),
        onClickTextButton: () =>
          handleRetrySaveVerdict({ key, rejectedReason, status }),
      })
    } finally {
      setSubmitting({ key: null, status: null })
    }
  }

  async function handleRetrySaveVerdict(verdict) {
    if (!submittingKeyRef.current) {
      await saveVerdict(verdict)
    }
  }

  function handleVerifyDocument(row) {
    return saveVerdict({
      key: row.key,
      status: APPLICANT_DOCUMENT_STATUS.verified,
    })
  }

  function handleRejectDocument(row) {
    setModal(
      <ApplicantDocumentRejectModal
        row={row}
        onClose={handleCloseModal}
        onSubmit={rejectedReason => {
          handleCloseModal()

          return saveVerdict({
            key: row.key,
            rejectedReason,
            status: APPLICANT_DOCUMENT_STATUS.rejected,
          })
        }}
      />
    )
  }

  return {
    handleRejectDocument,
    handleVerifyDocument,
    submittingKey,
    submittingStatus,
  }
}
